import { useCallback, useMemo } from "react";

import { useTranslation } from "@/lib/i18n";
import { UpdateWidgetBody } from "@/lib/api";
import { FormValues, WidgetTypes } from "@/types/widget";
import { useUpdateWidgetMutation } from "@/features/admin/features/widgets/mutations";
import { showToast } from "@/features/shared/components";
import { DynamicWidgetDialog } from "@/features/widget-forms/dialogs/dynamic-widget-dialog";
import { isUnchangedWidgetUpdate } from "@/features/widget-forms/dialogs/is-unchanged-widget-update";

type Props = {
  widgetId: string;
  pageId: string;
  initialValues: Required<FormValues>;
  onClose?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function UpdateWidgetDialog({
  pageId,
  widgetId,
  initialValues,
  onClose,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation();
  const updateWidgetMutation = useUpdateWidgetMutation();
  const clonedValues = useStructuredClone(initialValues);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    onClose?.();
  }, [onOpenChange, onClose]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (isUnchangedWidgetUpdate(clonedValues, values)) {
        handleClose();
        return;
      }
      await updateWidgetMutation.mutateAsync({
        pageId,
        widgetId,
        data: values as UpdateWidgetBody,
      });
      showToast(t("admin.messages.widget_updated"));
      handleClose();
    },
    [pageId, widgetId, clonedValues, handleClose, updateWidgetMutation, t],
  );

  const widgetType = initialValues.configuration?.type as WidgetTypes | undefined;
  if (!widgetType) {
    throw new Error("UpdateWidgetDialog must have initialValues.configuration.type set!");
  }

  return (
    <DynamicWidgetDialog
      type={widgetType}
      open={open}
      onOpenChange={onOpenChange}
      onReset={handleClose}
      onSubmit={onSubmit}
      initialValues={clonedValues}
    />
  );
}

function useStructuredClone<T>(value: T): T {
  return useMemo(() => {
    return structuredClone(value);
  }, [value]);
}
